import { CARD_H, CARD_W } from "./constants.js";
import { clamp } from "./numeric.js";

/* ===========================================================================
   Stage 5.2 — perspective normalisation onto the canonical raster.
   =========================================================================== */

/** Solve the 8x8 system for the homography mapping canonical corners onto `quad`. */
export function homographyToQuad(quad) {
  const dst = [
    [0, 0],
    [CARD_W - 1, 0],
    [CARD_W - 1, CARD_H - 1],
    [0, CARD_H - 1],
  ];
  const A = [],
    b = [];
  for (let i = 0; i < 4; i++) {
    const [x, y] = dst[i];
    const u = quad[i * 2],
      v = quad[i * 2 + 1];
    A.push([x, y, 1, 0, 0, 0, -x * u, -y * u]);
    b.push(u);
    A.push([0, 0, 0, x, y, 1, -x * v, -y * v]);
    b.push(v);
  }
  // Gaussian elimination with partial pivoting.
  for (let col = 0; col < 8; col++) {
    let pivot = col;
    for (let r = col + 1; r < 8; r++) if (Math.abs(A[r][col]) > Math.abs(A[pivot][col])) pivot = r;
    if (Math.abs(A[pivot][col]) < 1e-12) return null;
    [A[col], A[pivot]] = [A[pivot], A[col]];
    [b[col], b[pivot]] = [b[pivot], b[col]];
    for (let r = 0; r < 8; r++) {
      if (r === col) continue;
      const factor = A[r][col] / A[col][col];
      if (!factor) continue;
      for (let c = col; c < 8; c++) A[r][c] -= factor * A[col][c];
      b[r] -= factor * b[col];
    }
  }
  const h = b.map((value, i) => value / A[i][i]);
  return [h[0], h[1], h[2], h[3], h[4], h[5], h[6], h[7], 1];
}

/** Rectify the quadrilateral onto 488 x 680, bilinear, replicate-padded. */
export function warpCard(rgba, w, h, quad) {
  const H = homographyToQuad(quad);
  if (!H) return null;
  const out = new Uint8ClampedArray(CARD_W * CARD_H * 4);
  for (let y = 0; y < CARD_H; y++) {
    for (let x = 0; x < CARD_W; x++) {
      const denom = H[6] * x + H[7] * y + H[8];
      const sx = clamp((H[0] * x + H[1] * y + H[2]) / denom, 0, w - 1.001);
      const sy = clamp((H[3] * x + H[4] * y + H[5]) / denom, 0, h - 1.001);
      const x0 = Math.floor(sx),
        y0 = Math.floor(sy),
        fx = sx - x0,
        fy = sy - y0;
      const o = (y * CARD_W + x) * 4;
      for (let c = 0; c < 3; c++) {
        const p00 = rgba[(y0 * w + x0) * 4 + c],
          p01 = rgba[(y0 * w + x0 + 1) * 4 + c];
        const p10 = rgba[((y0 + 1) * w + x0) * 4 + c],
          p11 = rgba[((y0 + 1) * w + x0 + 1) * 4 + c];
        out[o + c] =
          p00 * (1 - fx) * (1 - fy) + p01 * fx * (1 - fy) + p10 * (1 - fx) * fy + p11 * fx * fy;
      }
      out[o + 3] = 255;
    }
  }
  return out;
}

export function rotate180(rgba, w, h) {
  const out = new Uint8ClampedArray(rgba.length);
  for (let i = 0, n = w * h; i < n; i++) {
    const s = i * 4,
      d = (n - 1 - i) * 4;
    out[d] = rgba[s];
    out[d + 1] = rgba[s + 1];
    out[d + 2] = rgba[s + 2];
    out[d + 3] = 255;
  }
  return out;
}
